import { Dispatch, SetStateAction, SyntheticEvent } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { beautifyEnum } from 'src/utils/utility';
import { CHARGE_CATEGORIES, CHARGE_NAMES, LEG_TYPES } from './constants';
import { ContractManagerColumnHeader } from '../ContractManagerColumnHeader';

// eslint-disable-next-line react-refresh/only-export-components
const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: '0',
    padding: '0',
    backgroundColor: 'transparent',
    width: '300px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderLeft: '0',
    borderRight: '0',
    borderTop: '0',
    borderColor: '#999999',
  },
  '& .MuiInputBase-input': {
    paddingTop: '4px!important',
    paddingBottom: '4px!important',
    paddingLeft: '0px!important',
    paddingRight: '0px!important',
    fontSize: '12px',
    [theme.breakpoints.up(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiFormHelperText-root ': {
    marginLeft: '0px!important',
    fontSize: '10px',
    [theme.breakpoints.up(1520)]: {
      fontSize: '12px',
    },
  },
}));

// Function to inject validation logic into columns that need it
export const injectValidation = (columns, validationErrors, setValidationErrors) => {
  return columns.map((column) => ({
    ...column,
    muiEditTextFieldProps: {
      ...column.muiEditTextFieldProps,
      error: !!validationErrors?.[column.accessorKey],
      helperText: validationErrors?.[column.accessorKey],
      onFocus: () =>
        setValidationErrors({
          ...validationErrors,
          [column.accessorKey]: undefined,
        }),
      sx: {
        // Style for regular text inputs
        '& .MuiInputBase-input': {
          fontSize: '12px',
          '@media (min-width: 1520px)': {
            fontSize: '14px',
          },
        },
        // Style specifically for MUI Select components
        '& .MuiSelect-select': {
          fontSize: '12px',
          '@media (min-width: 1520px)': {
            fontSize: '14px',
          },
        },
        // Style for form labels
        '& .MuiFormLabel-root': {
          fontSize: '12px',
          '@media (min-width: 1520px)': {
            fontSize: '14px',
          },
        },
        // Style for helper text
        '& .MuiFormHelperText-root': {
          fontSize: '10px',
          '@media (min-width: 1520px)': {
            fontSize: '12px',
          },
        },
        ...(column.muiEditTextFieldProps?.sx || {}),
      },
      // For select dropdowns, also style the dropdown menu
      ...(column.muiEditTextFieldProps?.select && {
        SelectProps: {
          MenuProps: {
            PaperProps: {
              sx: {
                '& .MuiMenuItem-root': {
                  fontSize: '12px',
                  '@media (min-width: 1520px)': {
                    fontSize: '14px',
                  },
                },
              },
            },
          },
        },
      }),
    },
  }));
};

export const getTableColumns = (
  customColumns: any[] = [],
  validationErrors: Record<string, string | undefined> = {},
  setValidationErrors: Dispatch<SetStateAction<Record<string, string | undefined>>> = () => {},
  options?: {
    filters?: Record<string, string[]>;
    handleFilter?: (columnId: string, selectedValues: string[]) => void;
    handleSort?: (columnId: string, direction: 'asc' | 'desc') => void;
    originalData?: any[];
    createFilterOptions?: any;
  }
) => {
  const commonColumns = [
    {
      accessorKey: 'chargeName',
      header: 'CHARGE NAME',
      enableSorting: false,
      Cell: ({ row }: any) => (
        <span className='text-12 xl:text-14'>{row?.original?.chargeName ? beautifyEnum(row.original.chargeName).toUpperCase() : '-'}</span>
      ),
      Header: ({ column }) => (
        <ContractManagerColumnHeader
          column={column}
          accessorKey='chargeName'
          originalData={options.originalData}
          currentFilters={options.filters.chargeName || []}
          onSort={options.handleSort}
          onFilter={options.handleFilter}
        />
      ),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Edit: ({ cell, column, row, table }) => {
        const { creatingRow, editingRow } = table.getState();
        const isCreating = creatingRow?.id === row.id;
        const isEditing = editingRow?.id === row.id;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const onChangeValue = (event: SyntheticEvent, value: string, reason: string) => {
          // eslint-disable-next-line no-underscore-dangle
          row._valuesCache[column.id] = value;
          if (isCreating) {
            table.setCreatingRow(row);
          } else if (isEditing) {
            table.setEditingRow(row);
          }
        };

        return (
          <div>
            <StyledAutocomplete
              freeSolo
              options={CHARGE_NAMES}
              defaultValue={row.original.chargeName}
              onInputChange={onChangeValue}
              componentsProps={{
                paper: {
                  sx: {
                    '& .MuiAutocomplete-option': {
                      fontSize: '12px',
                      '@media (min-width: 1520px)': {
                        fontSize: '14px',
                      },
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  name='chargeName'
                  {...params}
                  placeholder='CHARGE NAMES'
                  error={!!validationErrors.chargeName}
                  helperText={typeof validationErrors.chargeName !== 'undefined' && validationErrors.chargeName ? validationErrors.chargeName : ''}
                />
              )}
            />
          </div>
        );
      },
    },
    {
      accessorKey: 'chargeCategory',
      header: 'CHARGE CATEGORY',
      enableSorting: false,
      Cell: ({ row }: any) => (
        <span className='text-12 xl:text-14'>{row?.original?.chargeCategory ? beautifyEnum(row.original.chargeCategory).toUpperCase() : '-'}</span>
      ),
      Header: ({ column }) => (
        <ContractManagerColumnHeader
          column={column}
          accessorKey='chargeCategory'
          originalData={options.originalData}
          currentFilters={options.filters.chargeCategory || []}
          onSort={options.handleSort}
          onFilter={options.handleFilter}
        />
      ),
      editVariant: 'select',
      editSelectOptions: CHARGE_CATEGORIES,
      muiEditTextFieldProps: {
        select: true,
      },
    },
    {
      accessorKey: 'legType',
      header: 'LEG TYPE',
      enableSorting: false,
      Cell: ({ row }: any) => <span className='text-12 xl:text-14'>{row?.original?.legType ? beautifyEnum(row.original.legType).toUpperCase() : '-'}</span>,
      Header: ({ column }) => (
        <ContractManagerColumnHeader
          column={column}
          accessorKey='legType'
          originalData={options.originalData}
          currentFilters={options.filters.legType || []}
          onSort={options.handleSort}
          onFilter={options.handleFilter}
        />
      ),
      editVariant: 'select',
      editSelectOptions: LEG_TYPES,
      muiEditTextFieldProps: {
        select: true,
      },
    },
  ];

  const lastCommonColumns = [
    {
      header: 'ACCESSORIAL',
      id: `accessorial`,
      enableSorting: false,
      Header: ({ column }) => (
        <ContractManagerColumnHeader
          column={column}
          accessorKey='accessorial'
          filterOptions={options.createFilterOptions(options.originalData, 'accessorial', (item: any) => (item.accessorial ? 'True' : 'False'))}
          currentFilters={options.filters.accessorial || []}
          onSort={options.handleSort}
          onFilter={options.handleFilter}
        />
      ),
      accessorFn: (rowData) => (rowData.accessorial ? 'True' : 'False'),
      editVariant: 'select',
      editSelectOptions: ['True', 'False'],
      muiEditTextFieldProps: {
        select: true,
      },
    },
    {
      header: 'APPROVAL',
      id: `approval`,
      enableSorting: false,
      Header: ({ column }) => (
        <ContractManagerColumnHeader
          column={column}
          accessorKey='approval'
          filterOptions={options.createFilterOptions(options.originalData, 'approval', (item: any) => (item.approval ? 'True' : 'False'))}
          currentFilters={options.filters.approval || []}
          onSort={options.handleSort}
          onFilter={options.handleFilter}
        />
      ),
      accessorFn: (rowData) => (rowData.approval ? 'True' : 'False'),
      editVariant: 'select',
      editSelectOptions: ['True', 'False'],
      muiEditTextFieldProps: {
        select: true,
      },
    },
    {
      header: 'OUTLAY',
      id: `outlay`,
      enableSorting: false,
      Header: ({ column }) => (
        <ContractManagerColumnHeader
          column={column}
          accessorKey='outlay'
          filterOptions={options.createFilterOptions(options.originalData, 'outlay', (item: any) => (item.outlay ? 'True' : 'False'))}
          currentFilters={options.filters.outlay || []}
          onSort={options.handleSort}
          onFilter={options.handleFilter}
        />
      ),
      accessorFn: (rowData) => (rowData.outlay ? 'True' : 'False'),
      editVariant: 'select',
      editSelectOptions: ['True', 'False'],
      muiEditTextFieldProps: {
        select: true,
      },
    },
    {
      header: 'EXCLUDED FROM AUDIT',
      id: 'deviationExcluded',
      enableSorting: false,
      Header: ({ column }) => (
        <ContractManagerColumnHeader
          column={column}
          accessorKey='deviationExcluded'
          filterOptions={options.createFilterOptions(options.originalData, 'deviationExcluded', (item: any) => (item.deviationExcluded ? 'True' : 'False'))}
          currentFilters={options.filters.deviationExcluded || []}
          onSort={options.handleSort}
          onFilter={options.handleFilter}
        />
      ),
      accessorFn: (rowData) => (rowData.deviationExcluded ? 'True' : 'False'),
      editVariant: 'select',
      editSelectOptions: ['True', 'False'],
      muiEditTextFieldProps: {
        select: true,
      },
    },
  ];

  const allColumns = [...commonColumns, ...customColumns, ...lastCommonColumns];

  return injectValidation(allColumns, validationErrors, setValidationErrors);
};
